import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaFileMedical, FaCheckCircle, FaDollarSign, FaFileArchive, FaUpload, FaTrash, FaFilePdf } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { normalizarRut } from '../../utils/rutUtils';
import '../../styles/pages/RRHH/LicenciasMedicasPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de Licencias Médicas
 * Gestión de licencias médicas y trámites COMPIN
 */
const LicenciasMedicasPage = ({ token, id_company }) => {
    const [licencias, setLicencias] = useState([]);
    const [colaboradores, setColaboradores] = useState([]); // Todos (para filtros)
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [licenciaActual, setLicenciaActual] = useState(null);
    const [extractingPdf, setExtractingPdf] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('');

    // Formulario
    const [formData, setFormData] = useState({
        id_colaborador: '',
        numero_licencia: '',
        tipo_licencia: 'ENFERMEDAD_COMUN',
        fecha_emision: '',
        fecha_inicio: '',
        fecha_termino: '',
        dias_totales: '',
        diagnostico: '',
        nombre_medico: '',
        rut_medico: '',
        establecimiento: '',
        reposo_total: true,
        reposo_parcial: false,
        es_prorroga: false,
        licencia_original_id: null,
        pdf_url: ''
    });

    const fetchColaboradores = useCallback(async () => {
        try {
            console.log('Realizando petición de colaboradores...');
            // Cargar todos los colaboradores activos (para filtros)
            const responseAll = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: 'ACTIVO' },
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('Respuesta de colaboradores:', responseAll.data);

            if (responseAll.data.success) {
                setColaboradores(responseAll.data.data);
                console.log(`${responseAll.data.data.length} colaboradores cargados`);
            }

            // Cargar colaboradores disponibles (sin finiquito pagado)
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: 'ACTIVO', excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
            console.error('Detalles del error:', error.response?.data);
            Swal.fire('Error', 'No se pudieron cargar los colaboradores', 'error');
        }
    }, [id_company, token]);

    const fetchLicencias = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company };
            if (filtroEstado) params.estado_compin = filtroEstado;

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/licencias-medicas`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLicencias(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar licencias:', error);
            Swal.fire('Error', 'No se pudieron cargar las licencias médicas', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, filtroEstado, token]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchLicencias();
    }, [fetchLicencias]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const url = modoEdicion
                ? `${API_BASE_URL}/api/rrhh/licencias-medicas/${licenciaActual.id}`
                : `${API_BASE_URL}/api/rrhh/licencias-medicas`;

            const method = modoEdicion ? 'put' : 'post';

            const response = await axios[method](url, {
                ...formData,
                id_company
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                Swal.fire('Éxito', `Licencia médica ${modoEdicion ? 'actualizada' : 'registrada'} correctamente`, 'success');
                setShowModal(false);
                resetForm();
                fetchLicencias();
            }
        } catch (error) {
            console.error('Error al guardar licencia:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleEdit = (licencia) => {
        setModoEdicion(true);
        setLicenciaActual(licencia);
        setFormData({
            id_colaborador: licencia.id_colaborador,
            numero_licencia: licencia.numero_licencia || '',
            tipo_licencia: licencia.tipo_licencia || 'ENFERMEDAD_COMUN',
            fecha_emision: licencia.fecha_emision?.split('T')[0] || '',
            fecha_inicio: licencia.fecha_inicio?.split('T')[0] || '',
            fecha_termino: licencia.fecha_termino?.split('T')[0] || '',
            dias_totales: licencia.dias_totales || '',
            diagnostico: licencia.diagnostico || '',
            nombre_medico: licencia.nombre_medico || '',
            rut_medico: licencia.rut_medico || '',
            establecimiento: licencia.establecimiento || '',
            reposo_total: licencia.reposo_total !== false,
            reposo_parcial: licencia.reposo_parcial || false,
            es_prorroga: licencia.es_prorroga || false,
            licencia_original_id: licencia.licencia_original_id || null,
            pdf_url: licencia.pdf_url || ''
        });
        setShowModal(true);
    };

    const handleRegistrarSubsidio = async (id) => {
        try {
            const { value: montoSubsidio } = await Swal.fire({
                title: 'Registrar Subsidio',
                input: 'number',
                inputLabel: 'Monto del subsidio',
                inputPlaceholder: 'Ingrese el monto...',
                showCancelButton: true,
                confirmButtonText: 'Registrar',
                cancelButtonText: 'Cancelar'
            });

            if (montoSubsidio) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${id}/subsidio`,
                    { monto_subsidio: montoSubsidio },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Subsidio registrado correctamente', 'success');
                    fetchLicencias();
                }
            }
        } catch (error) {
            console.error('Error al registrar subsidio:', error);
            Swal.fire('Error', 'No se pudo registrar el subsidio', 'error');
        }
    };

    const handleCerrar = async (id) => {
        try {
            const result = await Swal.fire({
                title: '¿Cerrar licencia médica?',
                text: 'Esta acción marcará la licencia como cerrada',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, cerrar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${id}/cerrar`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Licencia cerrada correctamente', 'success');
                    fetchLicencias();
                }
            }
        } catch (error) {
            console.error('Error al cerrar licencia:', error);
            Swal.fire('Error', 'No se pudo cerrar la licencia', 'error');
        }
    };

    const handleEliminar = async (id) => {
        try {
            const result = await Swal.fire({
                title: '¿Eliminar licencia médica?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.delete(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Eliminada', 'La licencia médica fue eliminada', 'success');
                    fetchLicencias();
                }
            }
        } catch (error) {
            console.error('Error al eliminar licencia:', error);
            Swal.fire('Error', 'No se pudo eliminar la licencia', 'error');
        }
    };

    /**
     * Cargar PDF de licencia médica y extraer datos con IA
     */
    const handleExtractFromPdf = async () => {
        // Crear input file oculto
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf';

        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            if (!file.name.toLowerCase().endsWith('.pdf')) {
                Swal.fire('Error', 'Por favor seleccione un archivo PDF', 'error');
                return;
            }

            setExtractingPdf(true);
            Swal.fire({
                title: 'Procesando PDF...',
                html: 'Extrayendo datos de la licencia médica con IA',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                const formDataUpload = new FormData();
                formDataUpload.append('pdf', file);
                formDataUpload.append('id_company', id_company);

                const response = await axios.post(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/extract-from-pdf`,
                    formDataUpload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.data.success) {
                    const datos = response.data.data;
                    const datosRaw = response.data.raw_extraction;

                    // Buscar colaborador por RUT (normalizado para evitar problemas de formato)
                    let colaboradorEncontrado = null;
                    if (datosRaw.rut_trabajador) {
                        const rutBuscado = normalizarRut(datosRaw.rut_trabajador);
                        colaboradorEncontrado = colaboradores.find(
                            col => normalizarRut(col.rut) === rutBuscado
                        );
                    }

                    // Llenar el formulario con los datos extraídos
                    setFormData({
                        id_colaborador: colaboradorEncontrado ? colaboradorEncontrado.id : '',
                        numero_licencia: datos.numero_licencia || '',
                        tipo_licencia: datos.tipo_licencia || 'ENFERMEDAD_COMUN',
                        fecha_emision: datos.fecha_emision || '',
                        fecha_inicio: datos.fecha_inicio || '',
                        fecha_termino: datos.fecha_termino || '',
                        dias_totales: datos.dias_totales || '',
                        diagnostico: '',
                        nombre_medico: datos.nombre_medico || '',
                        rut_medico: '',
                        establecimiento: datos.establecimiento || '',
                        reposo_total: datos.reposo_total !== false,
                        reposo_parcial: datos.reposo_parcial || false,
                        es_prorroga: false,
                        licencia_original_id: null,
                        pdf_url: datos.pdf_url || ''
                    });

                    setModoEdicion(false);
                    setLicenciaActual(null);
                    setShowModal(true);

                    Swal.fire({
                        icon: 'success',
                        title: 'Datos extraídos',
                        html: `
                            <p><strong>Trabajador:</strong> ${datosRaw.nombre_trabajador || 'No identificado'}</p>
                            <p><strong>RUT:</strong> ${datosRaw.rut_trabajador || 'No identificado'}</p>
                            <p><strong>Folio:</strong> ${datos.numero_licencia || 'N/A'}</p>
                            <p><strong>Días:</strong> ${datos.dias_totales || 'N/A'}</p>
                            ${!colaboradorEncontrado ? '<p style="color: #e74c3c; margin-top: 10px;"><strong>⚠️ Colaborador no encontrado en el sistema. Selecciónelo manualmente.</strong></p>' : '<p style="color: #27ae60; margin-top: 10px;">✓ Colaborador encontrado y seleccionado</p>'}
                        `,
                        confirmButtonText: 'Continuar'
                    });
                }
            } catch (error) {
                console.error('Error al extraer datos del PDF:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'No se pudieron extraer los datos del PDF', 'error');
            } finally {
                setExtractingPdf(false);
            }
        };

        fileInput.click();
    };

    const resetForm = () => {
        setFormData({
            id_colaborador: '',
            numero_licencia: '',
            tipo_licencia: 'ENFERMEDAD_COMUN',
            fecha_emision: '',
            fecha_inicio: '',
            fecha_termino: '',
            dias_totales: '',
            diagnostico: '',
            nombre_medico: '',
            rut_medico: '',
            establecimiento: '',
            reposo_total: true,
            reposo_parcial: false,
            es_prorroga: false,
            licencia_original_id: null,
        });
        setModoEdicion(false);
        setLicenciaActual(null);
    };

    /**
     * Descargar ZIP con documentos para la licencia médica
     * Incluye: 3 últimas liquidaciones (según fecha de la licencia) + contrato vigente
     */
    const handleDescargarDocumentosZip = async (licenciaId) => {
        try {
            Swal.fire({
                title: 'Preparando documentos...',
                html: 'Generando ZIP con liquidaciones y contrato',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/licencias-medicas/${licenciaId}/documentos-zip`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );

            // Crear URL del blob y descargar
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Obtener nombre del archivo del header o usar uno por defecto
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'Documentos_Licencia.zip';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match) filename = match[1];
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                icon: 'success',
                title: 'Descarga completada',
                text: 'El ZIP incluye las liquidaciones de los 3 meses anteriores a la licencia y el contrato vigente',
                timer: 3000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error al descargar documentos:', error);

            if (error.response?.status === 404) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Sin documentos',
                    text: 'No hay documentos PDF disponibles para esta licencia'
                });
            } else {
                Swal.fire('Error', 'No se pudieron descargar los documentos', 'error');
            }
        }
    };

    return (
        <div className="licencias-container">
            <div className="licencias-header">
                <h1><FaFileMedical /> Licencias Médicas</h1>
                <div className="header-buttons">
                    <button
                        className="btn-upload-pdf"
                        onClick={handleExtractFromPdf}
                        disabled={extractingPdf}
                        title="Cargar PDF de licencia médica y extraer datos automáticamente"
                    >
                        <FaUpload /> {extractingPdf ? 'Procesando...' : 'Cargar PDF'}
                    </button>
                    <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                        <FaPlus /> Nueva Licencia
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="licencias-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="SIN_TRAMITAR">Sin tramitar</option>
                        <option value="TRAMITADO">Tramitado</option>
                    </select>
                </div>
            </div>

            {/* Tabla de licencias */}
            {loading ? (
                <div className="loading">Cargando licencias médicas...</div>
            ) : (
                <div className="licencias-table-container">
                    <table className="licencias-table">
                        <thead>
                            <tr>
                                <th>RUT</th>
                                <th>Colaborador</th>
                                <th>Tipo</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Término</th>
                                <th>Días</th>
                                <th>Folio COMPIN</th>
                                <th>Estado</th>
                                <th>Documentos</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {licencias.length === 0 ? (
                                <tr>
                                    <td colSpan="10" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron licencias médicas
                                    </td>
                                </tr>
                            ) : (
                                licencias.map((lic) => (
                                    <tr key={lic.id}>
                                        <td>{lic.rut}</td>
                                        <td>{lic.nombres} {lic.apellido_paterno}</td>
                                        <td>{lic.tipo_licencia?.replace(/_/g, ' ')}</td>
                                        <td>{new Date(lic.fecha_inicio).toLocaleDateString('es-CL')}</td>
                                        <td>{new Date(lic.fecha_termino).toLocaleDateString('es-CL')}</td>
                                        <td>{lic.dias_totales}</td>
                                        <td>{lic.numero_licencia || '-'}</td>
                                        <td>
                                            {lic.estado_compin ? (
                                                <span className={`badge badge-${lic.estado_compin?.toLowerCase()}`}>
                                                    {lic.estado_compin}
                                                </span>
                                            ) : (
                                                <span className="badge badge-pendiente">SIN TRAMITAR</span>
                                            )}
                                        </td>
                                        <td className="docs-cell">
                                            <button
                                                className="btn-download-zip"
                                                onClick={() => handleDescargarDocumentosZip(lic.id)}
                                                title="Descargar ZIP con liquidaciones (3 meses anteriores) y contrato"
                                            >
                                                <FaFileArchive /> Descargar ZIP
                                            </button>
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn-edit" onClick={() => handleEdit(lic)} title="Editar">
                                                <FaEdit />
                                            </button>
                                            {lic.pdf_url && (
                                                <button
                                                    className="btn-ver-pdf"
                                                    onClick={() => window.open(lic.pdf_url, '_blank')}
                                                    title="Ver PDF de la licencia"
                                                >
                                                    <FaFilePdf />
                                                </button>
                                            )}
                                            {lic.estado_compin === 'APROBADA' && !lic.monto_subsidio && (
                                                <button
                                                    className="btn-subsidio"
                                                    onClick={() => handleRegistrarSubsidio(lic.id)}
                                                    title="Registrar subsidio"
                                                >
                                                    <FaDollarSign />
                                                </button>
                                            )}
                                            {lic.estado_compin !== 'TRAMITADO' && (
                                                <button
                                                    className="btn-cerrar"
                                                    onClick={() => handleCerrar(lic.id)}
                                                    title="Cerrar licencia"
                                                >
                                                    <FaCheckCircle />
                                                </button>
                                            )}
                                            <button
                                                className="btn-eliminar"
                                                onClick={() => handleEliminar(lic.id)}
                                                title="Eliminar licencia"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content licencias-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{modoEdicion ? 'Editar Licencia Médica' : 'Nueva Licencia Médica'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h3>Información del Trabajador</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Colaborador *</label>
                                        <select
                                            value={formData.id_colaborador}
                                            onChange={(e) => setFormData({...formData, id_colaborador: e.target.value})}
                                            required
                                            disabled={modoEdicion}
                                        >
                                            <option value="">Seleccione...</option>
                                            {(modoEdicion ? colaboradores : colaboradoresDisponibles).map(col => (
                                                <option key={col.id} value={col.id}>
                                                    {col.nombres} {col.apellido_paterno}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Número de Licencia</label>
                                        <input
                                            type="text"
                                            value={formData.numero_licencia}
                                            onChange={(e) => setFormData({...formData, numero_licencia: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Tipo de Licencia *</label>
                                        <select
                                            value={formData.tipo_licencia}
                                            onChange={(e) => setFormData({...formData, tipo_licencia: e.target.value})}
                                            required
                                        >
                                            <option value="ENFERMEDAD_COMUN">Enfermedad Común</option>
                                            <option value="ACCIDENTE_TRABAJO">Accidente del Trabajo</option>
                                            <option value="ENFERMEDAD_PROFESIONAL">Enfermedad Profesional</option>
                                            <option value="MATERNAL_PRENATAL">Maternal Prenatal</option>
                                            <option value="MATERNAL_POSTNATAL">Maternal Postnatal</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Fechas y Duración</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Fecha Emisión *</label>
                                        <input
                                            type="date"
                                            value={formData.fecha_emision}
                                            onChange={(e) => setFormData({...formData, fecha_emision: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Fecha Inicio *</label>
                                        <input
                                            type="date"
                                            value={formData.fecha_inicio}
                                            onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Fecha Término *</label>
                                        <input
                                            type="date"
                                            value={formData.fecha_termino}
                                            onChange={(e) => setFormData({...formData, fecha_termino: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Días Totales *</label>
                                        <input
                                            type="number"
                                            value={formData.dias_totales}
                                            onChange={(e) => setFormData({...formData, dias_totales: e.target.value})}
                                            required
                                            min="1"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Tipo de Reposo</h3>
                                <div className="checkboxes-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.reposo_total}
                                            onChange={(e) => setFormData({...formData, reposo_total: e.target.checked})}
                                        />
                                        Reposo Total
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.reposo_parcial}
                                            onChange={(e) => setFormData({...formData, reposo_parcial: e.target.checked})}
                                        />
                                        Reposo Parcial
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.es_prorroga}
                                            onChange={(e) => setFormData({...formData, es_prorroga: e.target.checked})}
                                        />
                                        Es Prórroga
                                    </label>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LicenciasMedicasPage;
